import React, { useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom/dist';
import AddBrand from '../../core/modals/inventory/addbrand';
import EditBrand from '../../core/modals/inventory/editbrand';
import { userActions } from "../../core/action/userAction";
import Swal from 'sweetalert2';
import Select from 'react-select';
import Sliders from 'feather-icons-react/build/IconComponents/Sliders';
import { ChevronUp, Filter, PlusCircle, RotateCcw, StopCircle, Calendar } from 'feather-icons-react/build/IconComponents';
import { DatePicker } from 'antd';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import ImageWithBasePath from '../../core/img/imagewithbasebath';
import { setToogleHeader } from '../../core/redux/action';
import { brandActions } from "../../core/action/brandaction";
import moment from 'moment';
import { Eye } from 'feather-icons-react/build/IconComponents'; // Import Eye icon for view

const BrandList = () => {
    const dataSource = useSelector((state) => state.brand_list || []);
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);

    // State for filters
    const [isFilterVisible, setIsFilterVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        status: '',
        date: ''
    });
    const [selectedDate, setSelectedDate] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
    });
    const [sortConfig, setSortConfig] = useState({ value: 'newest', label: 'Newest' });

    // Filter brands client-side based on search term
    const getFilteredBrands = React.useMemo(() => {
        if (!searchTerm || !dataSource || dataSource.length === 0) {
            return dataSource;
        }

        const searchLower = searchTerm.toLowerCase().trim();

        return dataSource.filter(brand => {
            const name = (brand.brand_name || brand.name || '').toLowerCase();
            const description = (brand.description || '').toLowerCase();

            // Exact name match has highest priority
            if (name === searchLower) return true;

            // Name starts with search term
            if (name.startsWith(searchLower)) return true;

            // Description contains search term
            if (description.includes(searchLower)) return true;

            return false;
        });
    }, [searchTerm, dataSource]);

    // State for view modal
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedBrand, setSelectedBrand] = useState(null);

    const oldandlatestvalue = [
        { value: "date", label: "Sort by Date" },
        { value: "newest", label: "Newest" },
        { value: "oldest", label: "Oldest" },
    ];

    const statusOptions = [
        { value: "", label: "Choose Status" },
        { value: "Active", label: "Active" },
        { value: "InActive", label: "InActive" },
    ];

    // Check permissions - Use the permission system
    const canCreate = userActions.hasPermission('create-brands');
    const canEdit = userActions.hasPermission('edit-brands');
    const canDelete = userActions.hasPermission('delete-brands');
    const canView = userActions.hasPermission('view-brands');

    // Get user role for super admin check
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isSuperAdmin = user.role_slug === 'super-admin';

    // Fetch brands with useCallback
    const fetchBrands = useCallback(async (overrides = {}) => {
        // Check if user has view permission
        if (!canView && !isSuperAdmin) {
            console.warn('User does not have permission to view brands');
            return;
        }

        const activePage = overrides.page ?? pagination.page;
        const activeLimit = overrides.limit ?? pagination.limit;
        const activeSearch = overrides.search ?? searchTerm;
        const activeFilters = overrides.filters ?? filters;
        const activeSortConfig = overrides.sortConfig ?? sortConfig;

        setLoading(true);
        try {
            const params = {
                page: activePage,
                limit: activeLimit,
                search: activeSearch,
                status: activeFilters.status,
                start_date: activeFilters.date,
                end_date: activeFilters.date,
                sort_by: activeSortConfig.value === 'date' ? 'created_at' : 'created_at',
                sort_order: activeSortConfig.value === 'oldest' ? 'ASC' : 'DESC'
            };

            const result = await dispatch(brandActions.fetchBrands(params));

            const paginationData = result?.pagination || result?.data?.pagination;
            if (result.success && paginationData) {
                setPagination(prev => ({
                    ...prev,
                    page: paginationData.current_page || activePage,
                    limit: paginationData.items_per_page || activeLimit,
                    total: paginationData.total_items || 0,
                    totalPages: paginationData.total_pages || 1
                }));
            }
        } catch (error) {
            console.error('Error fetching brands:', error);
        } finally {
            setLoading(false);
        }
    }, [dispatch, pagination.page, pagination.limit, searchTerm, filters, sortConfig, canView, isSuperAdmin]);

    // Handle view brand
    const handleViewBrand = (record) => {
        setSelectedBrand(record);
        setShowViewModal(true);
    };

    // Handle edit brand
    const handleEditBrand = (record) => {
        if (!canEdit && !isSuperAdmin) {
            Swal.fire({
                icon: 'error',
                title: 'Access Denied',
                text: 'You do not have permission to edit brands',
                timer: 3000
            });
            return;
        }
        dispatch({ type: "selected_brand", payload: record });
    };

    // Handle delete brand
    const handleDeleteBrand = (record) => {
        if (!canDelete && !isSuperAdmin) {
            Swal.fire({
                icon: 'error',
                title: 'Access Denied',
                text: 'You do not have permission to delete brands',
                timer: 3000
            });
            return;
        }

        Swal.fire({
            title: 'Are you sure?',
            text: `Delete brand "${record.Brand}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        }).then(async (result) => {
            if (result.isConfirmed) {
                const response = await dispatch(brandActions.deleteBrand(record.id));
                if (response.success) {
                    fetchBrands();
                }
            }
        });
    };

    // Handle add new brand
    const handleAddBrand = () => {
        if (!canCreate && !isSuperAdmin) {
            Swal.fire({
                icon: 'error',
                title: 'Access Denied',
                text: 'You do not have permission to create brands',
                timer: 3000
            });
            return;
        }
        // Trigger the add brand modal
        document.querySelector('[data-bs-target="#add-brand"]')?.click();
    };

    // Handle search
    const handleSearch = (e) => {
        if (e.key === 'Enter' || e.type === 'click') {
            setPagination(prev => ({ ...prev, page: 1 }));
            fetchBrands({ page: 1 });
        }
    };

    // Handle filter change
    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: value
        }));
    };

    // Handle date change
    const handleDateChange = (date) => {
        setSelectedDate(date);
        if (date) {
            handleFilterChange('date', moment(date).format('YYYY-MM-DD'));
        } else {
            handleFilterChange('date', '');
        }
    };

    // Apply filters
    const handleApplyFilters = () => {
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchBrands({ page: 1 });
        setIsFilterVisible(false);
    };

    // Clear filters
    const handleClearFilters = () => {
        const emptyFilters = {
            status: '',
            date: ''
        };
        setFilters(emptyFilters);
        setSelectedDate(null);
        setSearchTerm('');
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchBrands({ page: 1, search: '', filters: emptyFilters });
    };

    // Handle sort change
    const handleSortChange = (option) => {
        setSortConfig(option);
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchBrands({ page: 1, sortConfig: option });
    };

    // Handle refresh
    const handleRefresh = () => {
        fetchBrands();
    };

    // Handle page change
    const handlePageChange = (page) => {
        setPagination(prev => ({ ...prev, page }));
        fetchBrands({ page });
    };

    // Handle limit change
    const handleLimitChange = (e) => {
        const nextLimit = parseInt(e.target.value, 10);
        setPagination(prev => ({
            ...prev,
            limit: nextLimit,
            page: 1
        }));
        fetchBrands({ page: 1, limit: nextLimit });
    };

    const columns = [
        {
            title: "ID",
            dataIndex: "id",
            sorter: true,
        },
        {
            title: "Brand",
            dataIndex: "Brand",
            sorter: true,
        },
        {
            title: "Brand Slug",
            dataIndex: "BrandSlug",
            sorter: true,
        },
        {
            title: "Created On",
            dataIndex: "CreateDate",
            sorter: true,
            render: (text) => text ? new Date(text).toLocaleDateString() : '-'
        },
        {
            title: "Status",
            dataIndex: "Status",
            render: (text) => (
                <span className={`badge ${text === 'Active' ? 'badge-linesuccess' : 'badge-linedanger'}`}>
                    {text}
                </span>
            ),
            sorter: true,
        },
        {
            title: 'Actions',
            dataIndex: 'actions',
            key: 'actions',
            render: (_text, record) => (
                <div className="edit-delete-action">
                    {/* View Button - Always show if user has view permission */}
                    {(canView || isSuperAdmin) && (
                        <button
                            className="btn btn-sm btn-outline-info me-2 p-2"
                            onClick={() => handleViewBrand(record)}
                            title="View Details"
                        >
                            <Eye size={14} />
                        </button>
                    )}

                    {/* Edit Button - Only show if user has edit permission */}
                    {(canEdit || isSuperAdmin) && (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-warning me-2 p-2"
                            data-bs-toggle="modal"
                            data-bs-target="#edit-brand"
                            onClick={() => handleEditBrand(record)}
                            title="Edit Brand"
                        >
                            <i data-feather="edit" className="feather-edit"></i>
                        </button>
                    )}

                    {/* Delete Button - Only show if user has delete permission */}
                    {(canDelete || isSuperAdmin) && (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-danger p-2"
                            onClick={() => handleDeleteBrand(record)}
                            title="Delete Brand"
                        >
                            <i data-feather="trash-2" className="feather-trash-2"></i>
                        </button>
                    )}
                </div>
            )
        }
    ];

    const renderRefreshTooltip = (props) => (
        <Tooltip id="refresh-tooltip" {...props}>
            Refresh
        </Tooltip>
    );
    const renderCollapseTooltip = (props) => (
        <Tooltip id="refresh-tooltip" {...props}>
            Collapse
        </Tooltip>
    );

    // Initial load
    useEffect(() => {
        if (canView || isSuperAdmin) {
            fetchBrands();
        }
        // Initial load only; subsequent loads are triggered by explicit user actions.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canView, isSuperAdmin]);

    return (
        <div>
            <div className="page-wrapper">
                <div className="content">
                    <div className="page-header">
                        <div className="add-item d-flex">
                            <div className="page-title">
                                <h4>Brand List</h4>
                                <h6>Manage your brands</h6>
                            </div>
                        </div>
                        <ul className="table-top-head">

                            <li>
                                <OverlayTrigger placement="top" overlay={renderRefreshTooltip}>
                                    <Link
                                        data-bs-toggle="tooltip"
                                        data-bs-placement="top"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleRefresh();
                                        }}
                                    >
                                        <RotateCcw />
                                    </Link>
                                </OverlayTrigger>
                            </li>
                            <li>
                                <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                                    <Link
                                        data-bs-toggle="tooltip"
                                        data-bs-placement="top"
                                        id="collapse-header"
                                        className={data ? "active" : ""}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            dispatch(setToogleHeader(!data));
                                        }}
                                    >
                                        <ChevronUp />
                                    </Link>
                                </OverlayTrigger>
                            </li>
                        </ul>

                        {/* Add New Brand Button - Only show if user has create permission */}
                        {(canCreate || isSuperAdmin) && (
                            <div className="page-btn">
                                <button
                                    className="btn btn-added"
                                    onClick={handleAddBrand}
                                    data-bs-toggle="modal"
                                    data-bs-target="#add-brand"
                                >
                                    <PlusCircle className="me-2" />
                                    Add New Brand
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="card table-list-card">
                        <div className="card-body">
                            {/* Only show filters and search if user has view permission */}
                            {(canView || isSuperAdmin) && (
                                <>
                                    <div className="table-top">
                                        <div className="search-set">
                                            <div className="search-input">
                                                <input
                                                    type="text"
                                                    placeholder="Search by brand name or slug"
                                                    className="form-control form-control-sm formsearch"
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
                                                />
                                                <Link
                                                    to="#"
                                                    className="btn btn-searchset"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleSearch(e);
                                                    }}
                                                >
                                                    <i data-feather="search" className="feather-search" />
                                                </Link>
                                            </div>
                                        </div>
                                        <div className="search-path">
                                            <Link
                                                className={`btn btn-filter ${isFilterVisible ? "setclose" : ""}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setIsFilterVisible(!isFilterVisible);
                                                }}
                                            >
                                                <Filter className="filter-icon" />
                                                <span>
                                                    <ImageWithBasePath src="assets/img/icons/closes.svg" alt="img" />
                                                </span>
                                            </Link>
                                        </div>
                                        <div className="form-sort">
                                            <Sliders className="info-img" />
                                            <Select
                                                className="select"
                                                options={oldandlatestvalue}
                                                value={sortConfig}
                                                onChange={handleSortChange}
                                                placeholder="Newest"
                                            />
                                        </div>
                                    </div>

                                    {/* Filter Section */}
                                    <div
                                        className={`card${isFilterVisible ? " visible" : ""}`}
                                        id="filter_inputs"
                                        style={{ display: isFilterVisible ? "block" : "none" }}
                                    >
                                        <div className="card-body pb-0">
                                            <div className="row">
                                                <div className="col-lg-3 col-sm-6 col-12">
                                                    <div className="input-blocks">
                                                        <StopCircle className="info-img" />
                                                        <Select
                                                            className="select"
                                                            options={statusOptions}
                                                            placeholder="Choose Status"
                                                            value={statusOptions.find(opt => opt.value === filters.status)}
                                                            onChange={(selected) => handleFilterChange('status', selected?.value || '')}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-lg-3 col-sm-6 col-12">
                                                    <div className="input-blocks">
                                                        <Calendar className="info-img" />
                                                        <div className="input-groupicon">
                                                            <DatePicker
                                                                selected={selectedDate}
                                                                onChange={handleDateChange}
                                                                className="filterdatepicker"
                                                                dateFormat="dd-MM-yyyy"
                                                                placeholder="Choose Date"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-lg-3 col-sm-6 col-12 ms-auto">
                                                    <div className="input-blocks d-flex gap-2">
                                                        <button
                                                            className="btn btn-filters"
                                                            onClick={handleApplyFilters}
                                                        >
                                                            <i data-feather="search" className="feather-search" /> Search
                                                        </button>
                                                        <button
                                                            className="btn btn-secondary"
                                                            onClick={handleClearFilters}
                                                        >
                                                            Clear
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Table */}
                            <div className="table-responsive">
                                {loading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-2">Loading brands...</p>
                                    </div>
                                ) : !canView && !isSuperAdmin ? (
                                    <div className="text-center py-5">
                                        <i data-feather="lock" className="feather-lock" style={{ fontSize: '48px', opacity: 0.5 }} />
                                        <h5 className="mt-3">Access Denied</h5>
                                        <p className="text-muted">You dont have permission to view brands</p>
                                    </div>
                                ) : getFilteredBrands.length === 0 ? (
                                    <div className="text-center py-5">
                                        <i data-feather="tag" className="feather-tag" style={{ fontSize: '48px', opacity: 0.5 }} />
                                        <h5 className="mt-3">No brands found</h5>
                                        <p className="text-muted">
                                            {searchTerm || Object.values(filters).some(v => v)
                                                ? "Try adjusting your search or filters"
                                                : "Add your first brand to get started"}
                                        </p>
                                        {(canCreate || isSuperAdmin) && (
                                            <button
                                                className="btn btn-primary mt-3"
                                                onClick={handleAddBrand}
                                                data-bs-toggle="modal"
                                                data-bs-target="#add-brand"
                                            >
                                                <PlusCircle size={16} className="me-2" />
                                                Add New Brand
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <table className="table table-hover">
                                            <thead>
                                                <tr>
                                                    {columns.map((col, index) => (
                                                        <th key={index} style={{ whiteSpace: 'nowrap' }}>{col.title}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {getFilteredBrands.map((record, rowIndex) => (
                                                    <tr key={record.id || rowIndex}>
                                                        {columns.map((col, colIndex) => (
                                                            <td key={colIndex}>
                                                                {col.render
                                                                    ? col.render(record[col.dataIndex], record, rowIndex)
                                                                    : record[col.dataIndex]}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>

                                        {/* Pagination */}
                                        {(() => {
                                            const filtered = getFilteredBrands;
                                            const isSearchActive = !!(searchTerm || Object.values(filters).some(v => v));
                                            const displayTotal = isSearchActive ? filtered.length : pagination.total;
                                            const showPageNav = !isSearchActive && pagination.totalPages > 1;

                                            return (displayTotal > 0 || pagination.total > 0) ? (
                                                <div className="pagination-wrapper mt-3">
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <div className="pagination-info text-muted">
                                                            {isSearchActive
                                                                ? `Showing ${filtered.length} matching result${filtered.length !== 1 ? 's' : ''}`
                                                                : `Showing ${((pagination.page - 1) * pagination.limit) + 1} to ${Math.min(pagination.page * pagination.limit, pagination.total)} of ${pagination.total} entries`
                                                            }
                                                        </div>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <select
                                                                className="form-select form-select-sm"
                                                                value={pagination.limit}
                                                                onChange={handleLimitChange}
                                                                style={{ width: 'auto' }}
                                                                disabled={loading}
                                                            >
                                                                <option value="10">10 per page</option>
                                                                <option value="20">20 per page</option>
                                                                <option value="50">50 per page</option>
                                                                <option value="100">100 per page</option>
                                                            </select>

                                                            {showPageNav && (
                                                                <nav>
                                                                    <ul className="pagination pagination-sm mb-0">
                                                                        <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                                                            <button
                                                                                className="page-link"
                                                                                onClick={() => handlePageChange(pagination.page - 1)}
                                                                                disabled={pagination.page === 1 || loading}
                                                                            >
                                                                                Previous
                                                                            </button>
                                                                        </li>

                                                                        {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                                                                            let pageNum;
                                                                            if (pagination.totalPages <= 5) {
                                                                                pageNum = i + 1;
                                                                            } else if (pagination.page <= 3) {
                                                                                pageNum = i + 1;
                                                                            } else if (pagination.page >= pagination.totalPages - 2) {
                                                                                pageNum = pagination.totalPages - 4 + i;
                                                                            } else {
                                                                                pageNum = pagination.page - 2 + i;
                                                                            }

                                                                            return (
                                                                                <li
                                                                                    key={pageNum}
                                                                                    className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}
                                                                                >
                                                                                    <button
                                                                                        className="page-link"
                                                                                        onClick={() => handlePageChange(pageNum)}
                                                                                        disabled={loading}
                                                                                    >
                                                                                        {pageNum}
                                                                                    </button>
                                                                                </li>
                                                                            );
                                                                        })}

                                                                        <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                                                                            <button
                                                                                className="page-link"
                                                                                onClick={() => handlePageChange(pagination.page + 1)}
                                                                                disabled={pagination.page === pagination.totalPages || loading}
                                                                            >
                                                                                Next
                                                                            </button>
                                                                        </li>
                                                                    </ul>
                                                                </nav>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : null;
                                        })()}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modals */}
            <AddBrand onSuccess={fetchBrands} />
            <EditBrand onSuccess={fetchBrands} />

            {/* View Brand Modal */}
            {showViewModal && selectedBrand && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setShowViewModal(false)}>
                    <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Brand Details</h5>
                                <button type="button" className="btn-close" onClick={() => setShowViewModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label fw-bold">ID:</label>
                                    <p>{selectedBrand.id}</p>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Brand Name:</label>
                                    <p>{selectedBrand.Brand}</p>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Brand Slug:</label>
                                    <p>{selectedBrand.BrandSlug}</p>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Status:</label>
                                    <p>
                                        <span className={`badge ${selectedBrand.Status === 'Active' ? 'badge-linesuccess' : 'badge-linedanger'}`}>
                                            {selectedBrand.Status}
                                        </span>
                                    </p>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Created On:</label>
                                    <p>{selectedBrand.CreateDate ? new Date(selectedBrand.CreateDate).toLocaleString() : '-'}</p>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowViewModal(false)}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default BrandList;